import { createAsyncThunk } from '@reduxjs/toolkit';

import { buildCatalogComplianceBody } from '../lib/buildCatalogComplianceBody';

import type {
    CatalogComplianceAddedDto,
    CatalogComplianceMarketplaceDto,
    CatalogComplianceRecategorizedDto,
    CatalogComplianceRemovedDto,
    CatalogComplianceSummaryDto,
    CatalogComplianceUncategorizedDto,
    ComplianceFilters,
} from '../types/ReportCatalogComplianceSchema';
import type { ThunkConfig } from 'app/providers/StoreProvider/config/types';

const BASE_PATH = '/v1/reports/catalog-compliance';

const createCatalogComplianceService = <ResponseDto>(
    name: string,
    endpoint: string,
) =>
    createAsyncThunk<ResponseDto, ComplianceFilters, ThunkConfig<string>>(
        `reportCatalogCompliance/${name}`,
        async (filters, thunkApi) => {
            const { extra, rejectWithValue } = thunkApi;

            try {
                const response = await extra.api.post<ResponseDto>(
                    `${BASE_PATH}/${endpoint}`,
                    buildCatalogComplianceBody(filters),
                );
                return response.data;
            } catch {
                return rejectWithValue('error');
            }
        },
    );

export const fetchCatalogComplianceSummary =
    createCatalogComplianceService<CatalogComplianceSummaryDto>(
        'fetchSummary',
        'summary',
    );

export const fetchCatalogComplianceMarketplacePurchases =
    createCatalogComplianceService<CatalogComplianceMarketplaceDto>(
        'fetchMarketplacePurchases',
        'marketplace-purchases',
    );

export const fetchCatalogComplianceItemsAdded =
    createCatalogComplianceService<CatalogComplianceAddedDto>(
        'fetchItemsAdded',
        'items-added',
    );

export const fetchCatalogComplianceItemsRemoved =
    createCatalogComplianceService<CatalogComplianceRemovedDto>(
        'fetchItemsRemoved',
        'items-removed',
    );

export const fetchCatalogComplianceItemsRecategorized =
    createCatalogComplianceService<CatalogComplianceRecategorizedDto>(
        'fetchItemsRecategorized',
        'items-recategorized',
    );

export const fetchCatalogComplianceItemsUncategorized =
    createCatalogComplianceService<CatalogComplianceUncategorizedDto>(
        'fetchItemsUncategorized',
        'items-uncategorized',
    );
